import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive, ArchiveRestore, Building2, Check, Code, Copy, Crown, Database, Upload, X,
} from "lucide-react";
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  MultiTagInput,
  Switch,
  Tabs, TabsList, TabsTrigger, TabsContent,
  ToggleGroup, ToggleGroupItem,
  cn,
} from "@nexus/ui";
import { ALL_OPPORTUNITY_TYPES_ENABLED, MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX, type AddPreflightMarketInput, type CurrencyCode, type EnabledOpportunityTypes, type MarketplaceOrderHandoffDefaults } from "@nexus/contracts";
import type { AdminTenantSettingsData, UserItem } from "@/features/identity-hub/api/identity-hub.api";
import {
  adminTenantPreflightMarketRefsQueryOptions,
  adminTenantSettingsQueryOptions,
  adminTenantSyncRunsQueryOptions,
  invalidateProductsForTenant,
} from "@/lib/queries";
import { orgColor } from "@/features/identity-hub/utils/iam.utils";
import {
  HandoffSectionProgress,
  MarketplaceOrderHandoffDefaultsForm,
  emptyHandoffDefaultsFormState,
  handoffDefaultsFromApi,
} from "@/features/marketplace/components/MarketplaceOrderHandoffDefaultsForm";
import {
  useUpdateTenant,
  useDeactivateTenant,
  useReactivateTenant,
  useAllIndustries,
  useAdminUpdateTenantSettings,
  useAdminTenantSettings,
  useReplaceTenantPreflightMarkets,
  useTriggerTenantSync,
} from "@/features/identity-hub/hooks/useIdentityHub";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { OrgLogoMark } from "./OrgLogoMark";
import { OrgKpiSettingsTab } from "./OrgKpiSettingsTab";
import { OrgIndustriesSettingsTable } from "./OrgIndustriesSettingsTable";
import { LegacyRetoolSettingsRow } from "./LegacyRetoolSettingsRow";
import { SettingsField, SettingsRow } from "./shared/SettingsRow";
import { industryLabel } from "./OrgCard";
import type { OrgItem } from "./OrgDrawer";
import { AccentColorControl } from "./shared/AccentColorControl";
import {
  SEGMENT_SLIDING_INDICATOR_CLASS,
  SEGMENT_SLIDING_ITEM_CLASS,
  SEGMENT_SLIDING_TRACK_CLASS,
} from "./shared/segment-toggle-classes";
import { OrgBigQuerySyncSection } from "./OrgBigQuerySyncSection";
import { MarketMultiSelect } from "./MarketMultiSelect";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";
import { bothCatalogsUpdated, catalogSnapshot } from "@/features/identity-hub/lib/org-settings-sync-catalog";
import {
  decideInboxSync,
  emailsFromRows,
  newInboxRow,
  rowsFromSaved,
  savedInboxEmails,
  type InboxRow,
} from "@/features/identity-hub/lib/marketplace-inbox-rows";
import { decideHandoffSync } from "@/features/identity-hub/lib/marketplace-handoff-sync";
import {
  orgSettingsChangedFields,
  orgSettingsChanges,
  resolveTenantProductLine,
  toIndustryDefaultBrandMap,
  type IndustryDefaultBrandMap,
  type OrgSettingsChangedField,
  type TenantProductLine,
} from "@/features/identity-hub/lib/org-settings-dirty";
import { saveOrgChanges } from "@/features/identity-hub/lib/org-settings-save";
import { latestSuccessfulRun } from "@/features/identity-hub/lib/bq-sync-display";
import { presignTenantLogoUpload, confirmTenantLogoUpload } from "@/features/identity-hub/api/tenant-logo-upload.api";
import { putToSignedUrlWithProgress, stageMemoryPreflightUpload } from "@/features/preflight/api/uploads.api";
import { TENANT_LOGO_ACCEPT, getTenantLogoMaxBytes, isAllowedTenantLogoType } from "@/features/identity-hub/lib/tenant-logo-upload-constraints";
import { CURRENCY_DROPDOWN_OPTIONS } from "@/lib/currency-display";
import { toast } from "sonner";

// ─── Constants ────────────────────────────────────────────────────────────────

const TENANT_TYPES = [
  { value: "ORGANIZATION", labelKey: "iam.orgDrawer.settings.tenantTypeOrganization", labelDefault: "Organization", Icon: Building2 },
  { value: "API", labelKey: "iam.orgDrawer.settings.tenantTypeApi", labelDefault: "API", Icon: Code },
] as const;

const PRODUCT_LINE_OPTIONS = [
  {
    value: "CORE",
    labelKey: "iam.orgDrawer.settings.productLineCore",
    labelDefault: "Core",
    hintKey: "iam.orgDrawer.settings.productLineCoreHint",
    hintDefault: "Own brand",
  },
  {
    value: "COMP",
    labelKey: "iam.orgDrawer.settings.productLineComp",
    labelDefault: "Comp",
    hintKey: "iam.orgDrawer.settings.productLineCompHint",
    hintDefault: "Competitors",
  },
  {
    value: "CORE_AND_COMP",
    labelKey: "iam.orgDrawer.settings.productLineCoreAndComp",
    labelDefault: "Core & Comp",
    hintKey: "iam.orgDrawer.settings.productLineCoreAndCompHint",
    hintDefault: "Both",
  },
] as const;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isValidUuid(s: string) { return UUID_RE.test(s); }

// Deliberately loose — the API's Zod `.email()` is the real gate. This only
// catches an obvious typo before the round trip.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(s: string) { return EMAIL_RE.test(s); }

const DESC_MAX = 500;
const ANIM_DURATION = 200;

const SETTINGS_CONTROL_CLASS =
  "h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50";

type SettingsTab = "general" | "organization" | "marketplace" | "kpis" | "danger";
export type OrgSettingsTab = SettingsTab;

export interface OrgSettingsFormApi {
  isDirty: boolean;
  changedFields: OrgSettingsChangedField[];
  lastBqSyncedAt: string | null;
  canSave: boolean;
  isSaving: boolean;
  handleSave: () => Promise<boolean>;
  saveHint: string;
  canSyncWithBq: boolean;
  isSyncingBq: boolean;
  isSavingPartitionForSync: boolean;
  syncPartitionEmpty: boolean;
  handleSyncWithBq: () => void;
}

// ─── Super-tenant lock overlay ─────────────────────────────────────────────────

function SuperTenantSettingsOverlay() {
  const { t } = useTranslation();

  return (
    <div
      className="absolute inset-0 flex items-center justify-center p-4"
      role="region"
      aria-label={t("iam.orgDrawer.settings.readOnlyOverlay.title", "Settings are locked")}
    >
      <div className="w-full max-w-[300px] rounded-xl border border-amber-500/30 bg-card/95 px-4 py-4 shadow-lg backdrop-blur-sm">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15">
          <Crown className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
        </div>
        <p className="text-sm font-semibold leading-snug text-foreground">
          {t("iam.orgDrawer.settings.readOnlyOverlay.title", "Settings are locked")}
        </p>
        <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
          {t(
            "iam.orgDrawer.settings.readOnlyOverlay.body",
            "Adge is the platform operator organization. Its profile is managed centrally and cannot be edited here.",
          )}
        </p>
        <ul className="mt-3 space-y-1.5 border-t border-border/60 pt-3 text-[11px] leading-relaxed text-muted-foreground">
          <li className="flex gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" aria-hidden />
            {t(
              "iam.orgDrawer.settings.readOnlyOverlay.reason1",
              "Protects cross-tenant administration and provisioning from accidental changes.",
            )}
          </li>
          <li className="flex gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" aria-hidden />
            {t(
              "iam.orgDrawer.settings.readOnlyOverlay.reason2",
              "Contact Adge engineering if a platform setting needs updating.",
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}

/** Dims + locks a settings region behind the super-tenant overlay. */
function LockableSection({ locked, children }: { locked: boolean; children: React.ReactNode }) {
  return (
    <div className={cn("relative", locked && "min-h-[320px]")}>
      <div
        className={cn(
          "flex flex-col",
          locked && "pointer-events-none select-none opacity-[0.35]",
        )}
        aria-hidden={locked}
      >
        {children}
      </div>
      {locked && <SuperTenantSettingsOverlay />}
    </div>
  );
}

// ─── OrgSettingsModal ───────────────────────────────────────────────────────────
//
// Centered modal for org settings, split into General / Organization / Marketplace / Danger tabs.
// Mounted only while open (conditional render at the parent) so every open — including
// same-org close→reopen — starts from fresh local state. A single Save in the footer
// commits a diff across all tabs.
//
// Sync lifecycle: closing the modal mid-sync unmounts it and stops the UI poll, but the
// backend Pub/Sub sync job keeps running. Reopening refetches admin settings and reflects
// any synced products. (See plan B-4 (c) / N-7 — the sync endpoint should be guarded
// server-side against concurrent runs.)

export interface OrgSettingsModalProps {
  org: OrgItem;
  isSuperTenant: boolean;
  onClose: () => void;
}

export interface OrgSettingsFormProps {
  org: OrgItem;
  isSuperTenant: boolean;
  onClose?: () => void;
  variant?: "dialog" | "embedded";
  tab?: OrgSettingsTab;
  onFormApiChange?: (api: OrgSettingsFormApi) => void;
  /** Org members offered as the BigQuery sync-failure alert recipient. */
  members?: UserItem[];
}

export function OrgSettingsModal({ org, isSuperTenant, onClose }: OrgSettingsModalProps) {
  return <OrgSettingsForm org={org} isSuperTenant={isSuperTenant} onClose={onClose} variant="dialog" />;
}

export function OrgSettingsForm({
  org,
  isSuperTenant,
  onClose,
  variant = "dialog",
  tab: tabProp,
  onFormApiChange,
  members = [],
}: OrgSettingsFormProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const canViewOrganizationTab = useIsSuperTenant();

  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState<SettingsTab>("general");
  const [idCopied, setIdCopied] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  // ── Settings state ────────────────────────────────────────────────
  const [settingsName, setSettingsName] = useState(org.name ?? "");
  const [settingsType, setSettingsType] = useState(org.type === "API" ? "API" : "ORGANIZATION");
  const [settingsProductLine, setSettingsProductLine] = useState<TenantProductLine>(
    resolveTenantProductLine(org.productLine),
  );
  const [settingsIndustryIndices, setSettingsIndustryIndices] = useState<string[]>(org.industryIndices ?? []);
  const [settingsDefaultIndustry, setSettingsDefaultIndustry] = useState<string | null>(org.defaultIndustry ?? null);
  const [settingsDefaultBrand, setSettingsDefaultBrand] = useState("");
  const [settingsIndustryDefaultBrands, setSettingsIndustryDefaultBrands] = useState<IndustryDefaultBrandMap>({});
  const [settingsColor, setSettingsColor] = useState<string | null>(org.brandColor?.trim() ?? null);
  const [settingsDescription, setSettingsDescription] = useState(org.description ?? "");
  const [settingsRetoolUserId, setSettingsRetoolUserId] = useState(org.retoolUserId ?? "");
  const [settingsRetoolTouched, setSettingsRetoolTouched] = useState(false);
  const [settingsCustomerPartition, setSettingsCustomerPartition] = useState("");
  const [settingsMarketplaceInboxRows, setSettingsMarketplaceInboxRows] = useState<InboxRow[]>([newInboxRow()]);
  const [settingsMarketplaceInboxTouched, setSettingsMarketplaceInboxTouched] = useState(false);
  const [settingsHandoffDefaultsTouched, setSettingsHandoffDefaultsTouched] = useState(false);
  const [settingsHandoffDefaults, setSettingsHandoffDefaults] = useState<MarketplaceOrderHandoffDefaults>(
    emptyHandoffDefaultsFormState(),
  );
  const [settingsConversionsMonthly, setSettingsConversionsMonthly] = useState(0);
  const [settingsConversionsWeekly, setSettingsConversionsWeekly] = useState(0);
  const [settingsCurrency, setSettingsCurrency] = useState<CurrencyCode>("USD");
  const [conversionsSaved, setConversionsSaved] = useState(false);
  const [settingsBqSyncFailureSlackAlertEnabled, setSettingsBqSyncFailureSlackAlertEnabled] = useState(true);
  const [settingsEnabledOpportunityTypes, setSettingsEnabledOpportunityTypes] = useState<EnabledOpportunityTypes>({
    ...ALL_OPPORTUNITY_TYPES_ENABLED,
  });
  const [settingsPreflightMarketRefs, setSettingsPreflightMarketRefs] = useState<AddPreflightMarketInput[]>([]);
  const [settingsPreflightMarketsTouched, setSettingsPreflightMarketsTouched] = useState(false);
  const [isSyncInFlight, setIsSyncInFlight] = useState(false);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const syncCatalogSnapshotRef = useRef(catalogSnapshot(undefined, undefined));
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ── Logo upload state ─────────────────────────────────────────────
  const [logoUploadId, setLogoUploadId] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState<number | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const updateTenantMut = useUpdateTenant();
  const deactivateTenantMut = useDeactivateTenant();
  const reactivateTenantMut = useReactivateTenant();
  const adminUpdateSettingsMut = useAdminUpdateTenantSettings();
  const triggerSyncMut = useTriggerTenantSync();
  const replaceMarketsMut = useReplaceTenantPreflightMarkets();
  const { data: adminSettings, isLoading: isLoadingAdminSettings } = useAdminTenantSettings(org.id);
  const { data: syncRunsData, isLoading: isLoadingSyncRuns } = useQuery(adminTenantSyncRunsQueryOptions(org.id));
  const { data: savedMarketRefs, isLoading: isLoadingMarketRefs } = useQuery({
    ...adminTenantPreflightMarketRefsQueryOptions(org.id),
    enabled: !!org.id && canViewOrganizationTab,
  });
  const { data: industries = [] } = useAllIndustries();

  useEffect(() => {
    if (savedMarketRefs && !settingsPreflightMarketsTouched) setSettingsPreflightMarketRefs(savedMarketRefs);
  }, [savedMarketRefs, settingsPreflightMarketsTouched]);

  const savedSettingsForDiff = useMemo(
    () => (adminSettings || savedMarketRefs ? { ...adminSettings, preflightMarketRefs: savedMarketRefs } : undefined),
    [adminSettings, savedMarketRefs],
  );

  const isArchived = !!org.deletedAt;

  // Seed customerPartition/conversions from the query cache immediately (avoids a flash),
  // then keep them in sync as the settings query resolves/updates.
  useEffect(() => {
    const cached = qc.getQueryData<AdminTenantSettingsData>(adminTenantSettingsQueryOptions(org.id).queryKey);
    if (cached) {
      setSettingsCustomerPartition(cached.customerPartition ?? "");
      setSettingsConversionsMonthly(cached.conversionsMonthly ?? 0);
      setSettingsConversionsWeekly(cached.conversionsWeekly ?? 0);
      setSettingsCurrency(cached.currency ?? "USD");
      setSettingsDefaultIndustry(cached.defaultIndustry ?? null);
      setSettingsDefaultBrand(cached.defaultBrand ?? "");
      setSettingsIndustryDefaultBrands(toIndustryDefaultBrandMap(cached.industryDefaultBrands));
      setSettingsMarketplaceInboxRows(rowsFromSaved(cached.marketplaceOrdersInboxEmails, cached.marketplaceOrdersInboxEmail));
      setSettingsHandoffDefaults(handoffDefaultsFromApi(cached.marketplaceOrderHandoffDefaults));
      setSettingsBqSyncFailureSlackAlertEnabled(cached.bqSyncFailureSlackAlertEnabled ?? true);
      setSettingsEnabledOpportunityTypes(cached.enabledOpportunityTypes ?? { ...ALL_OPPORTUNITY_TYPES_ENABLED });
    }
    // Run once on mount for this org.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (adminSettings) {
      setSettingsCustomerPartition(adminSettings.customerPartition ?? "");
      setSettingsConversionsMonthly(adminSettings.conversionsMonthly ?? 0);
      setSettingsConversionsWeekly(adminSettings.conversionsWeekly ?? 0);
      setSettingsCurrency(adminSettings.currency ?? "USD");
      setSettingsDefaultIndustry(adminSettings.defaultIndustry ?? null);
      setSettingsDefaultBrand(adminSettings.defaultBrand ?? "");
      setSettingsBqSyncFailureSlackAlertEnabled(adminSettings.bqSyncFailureSlackAlertEnabled ?? true);
      const savedInbox = savedInboxEmails(
        adminSettings.marketplaceOrdersInboxEmails,
        adminSettings.marketplaceOrdersInboxEmail,
      );
      const decision = decideInboxSync(
        settingsMarketplaceInboxTouched,
        emailsFromRows(settingsMarketplaceInboxRows),
        savedInbox,
      );
      if (decision === "reseed") {
        setSettingsMarketplaceInboxRows(rowsFromSaved(adminSettings.marketplaceOrdersInboxEmails, adminSettings.marketplaceOrdersInboxEmail));
      } else if (decision === "mark-pristine") {
        setSettingsMarketplaceInboxTouched(false);
      }
      const savedHandoff = handoffDefaultsFromApi(adminSettings.marketplaceOrderHandoffDefaults);
      const handoffDecision = decideHandoffSync(
        settingsHandoffDefaultsTouched,
        settingsHandoffDefaults,
        savedHandoff,
      );
      if (handoffDecision === "reseed") {
        setSettingsHandoffDefaults(savedHandoff);
      } else       if (handoffDecision === "mark-pristine") {
        setSettingsHandoffDefaultsTouched(false);
      }
    }
    // Inbox row identity and handoff field values are read at effect time; listing
    // them would re-run this effect on every keystroke and reset the other settings fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminSettings?.customerPartition,
    adminSettings?.conversionsMonthly,
    adminSettings?.conversionsWeekly,
    adminSettings?.currency,
    adminSettings?.defaultIndustry,
    adminSettings?.defaultBrand,
    adminSettings?.marketplaceOrdersInboxEmails,
    adminSettings?.marketplaceOrdersInboxEmail,
    adminSettings?.marketplaceOrderHandoffDefaults,
    adminSettings?.bqSyncFailureSlackAlertEnabled,
    settingsMarketplaceInboxTouched,
    settingsHandoffDefaultsTouched,
  ]);

  useEffect(() => {
    if (!adminSettings) return;
    setSettingsEnabledOpportunityTypes(
      adminSettings.enabledOpportunityTypes ?? { ...ALL_OPPORTUNITY_TYPES_ENABLED },
    );
  }, [adminSettings?.enabledOpportunityTypes]);

  useEffect(() => {
    if (!adminSettings) return;
    setSettingsIndustryDefaultBrands(toIndustryDefaultBrandMap(adminSettings.industryDefaultBrands));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSettings?.industryDefaultBrands]);

  // Stop sync polling helper
  const stopSyncPolling = () => {
    const wasPolling = syncPollRef.current !== undefined || syncTimeoutRef.current !== undefined;
    clearInterval(syncPollRef.current);
    clearTimeout(syncTimeoutRef.current);
    syncPollRef.current = undefined;
    syncTimeoutRef.current = undefined;
    if (wasPolling) {
      invalidateProductsForTenant(qc, org.id);
    }
  };

  // Detect catalog changes while sync is in flight
  useEffect(() => {
    if (!isSyncInFlight) return;
    const current = catalogSnapshot(adminSettings?.productTypes, adminSettings?.products);
    if (bothCatalogsUpdated(current, syncCatalogSnapshotRef.current)) {
      setIsSyncInFlight(false);
      stopSyncPolling();
    }
  }, [isSyncInFlight, adminSettings?.productTypes, adminSettings?.products]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopSyncPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived / validation ──────────────────────────────────────────
  const isLogoUploading = logoUploadProgress !== null;
  const displayLogoUrl = logoRemoved ? null : (logoPreviewUrl ?? (org.logoUrl ?? null));
  const accentColor = settingsColor ?? org.brandColor ?? orgColor(org.name);
  const descOver = settingsDescription.length > DESC_MAX;
  const productTypes = adminSettings?.productTypes ?? [];
  const skuProducts = adminSettings?.products ?? [];
  const retoolError = settingsRetoolUserId && !isValidUuid(settingsRetoolUserId)
    ? t("iam.validation.invalidUuid")
    : null;
  const marketplaceInboxError = settingsMarketplaceInboxRows.some((row) => {
    const trimmed = row.email.trim();
    return trimmed.length > 0 && !isValidEmail(trimmed);
  })
    ? t("iam.validation.invalidEmail", "Enter a valid email address")
    : null;
  const partitionIsEmpty = settingsCustomerPartition.trim().length === 0;
  const isSyncing = isSyncInFlight || triggerSyncMut.isPending;
  const isSavingPartition = adminUpdateSettingsMut.isPending;

  const canSave =
    !isSuperTenant &&
    settingsName.trim().length > 0 &&
    !descOver &&
    !retoolError &&
    !marketplaceInboxError &&
    !updateTenantMut.isPending &&
    !adminUpdateSettingsMut.isPending &&
    !replaceMarketsMut.isPending &&
    !isLogoUploading;

  // ── Close ─────────────────────────────────────────────────────────
  const requestClose = () => {
    if (variant === "embedded") {
      onClose?.();
      return;
    }
    setOpen(false);
    window.setTimeout(() => onClose?.(), ANIM_DURATION);
  };

  const handleCopyId = () => {
    void navigator.clipboard.writeText(org.id).then(() => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 1800);
    });
  };

  // ── Logo handlers ─────────────────────────────────────────────────
  const handleLogoFileChange = async (file: File | undefined) => {
    if (!file || !activeTenantId) return;

    if (!isAllowedTenantLogoType(file.type)) {
      toast.error(t("iam.orgDrawer.settings.logoInvalidType", "Use PNG, JPEG, or WebP"));
      return;
    }
    const maxBytes = getTenantLogoMaxBytes(file.type);
    if (maxBytes != null && file.size > maxBytes) {
      toast.error(t("iam.orgDrawer.settings.logoTooLarge", "Logo file is too large"));
      return;
    }

    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setLogoRemoved(false);
    setLogoUploadProgress(0);

    try {
      const presign = await presignTenantLogoUpload({
        tenantId: org.id,
        contentType: file.type,
        sizeBytes: file.size,
        originalName: file.name,
      });

      if (presign.requiresMemoryStage || presign.requiresServerStage) {
        await stageMemoryPreflightUpload({
          tenantId: org.id,
          uploadId: presign.uploadId,
          contentType: file.type,
          file,
        });
        setLogoUploadProgress(100);
      } else {
        await putToSignedUrlWithProgress(presign.uploadUrl, file, presign.requiredHeaders, {
          onProgress: setLogoUploadProgress,
        });
      }

      const confirmed = await confirmTenantLogoUpload({
        tenantId: org.id,
        uploadId: presign.uploadId,
      });

      setLogoUploadId(confirmed.uploadId);
      toast.success(t("iam.orgDrawer.settings.logoUploaded", "Logo uploaded"));
    } catch {
      toast.error(t("iam.orgDrawer.settings.logoUploadFailed", "Logo upload failed"));
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl(null);
    } finally {
      setLogoUploadProgress(null);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleLogoRemove = () => {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(null);
    setLogoUploadId(null);
    setLogoRemoved(true);
  };

  // ── Upstream sync ─────────────────────────────────────────────────
  const handleTriggerSync = () => {
    const trimmedPartition = settingsCustomerPartition.trim();
    if (trimmedPartition.length === 0) return;

    const currentPartition = adminSettings?.customerPartition ?? "";
    const partitionIsDirty = trimmedPartition !== currentPartition;

    const startSyncWithPolling = () => {
      syncCatalogSnapshotRef.current = catalogSnapshot(
        adminSettings?.productTypes,
        adminSettings?.products,
      );
      setIsSyncInFlight(true);

      triggerSyncMut.mutate(org.id, {
        onSuccess: () => {
          syncPollRef.current = setInterval(() => {
            void qc.invalidateQueries({ queryKey: ["identity-hub", "tenant-settings", org.id] });
            void qc.invalidateQueries({ queryKey: ["identity-hub", "tenant-sync-runs", org.id] });
          }, 3000);

          syncTimeoutRef.current = setTimeout(() => {
            setIsSyncInFlight(false);
            stopSyncPolling();
          }, 30_000);
        },
        onError: () => {
          setIsSyncInFlight(false);
        },
      });
    };

    if (partitionIsDirty) {
      adminUpdateSettingsMut.mutate(
        { tenantId: org.id, data: { customerPartition: trimmedPartition } },
        { onSuccess: () => startSyncWithPolling() },
      );
    } else {
      startSyncWithPolling();
    }
  };

  const orgSettingsFormSnapshot = useMemo(
    () => ({
      name: settingsName,
      description: settingsDescription,
      brandColor: settingsColor,
      type: settingsType,
      productLine: settingsProductLine,
      retoolUserId: settingsRetoolUserId,
      industryIndices: settingsIndustryIndices,
      logoUploadId,
      logoRemoved,
      defaultIndustry: settingsDefaultIndustry,
      defaultBrand: settingsDefaultBrand,
      industryDefaultBrands: settingsIndustryDefaultBrands,
      customerPartition: settingsCustomerPartition,
      inboxEmails: emailsFromRows(settingsMarketplaceInboxRows),
      handoffDefaults: settingsHandoffDefaults,
      conversionsMonthly: settingsConversionsMonthly,
      conversionsWeekly: settingsConversionsWeekly,
      currency: settingsCurrency,
      bqSyncFailureSlackAlertEnabled: settingsBqSyncFailureSlackAlertEnabled,
      enabledOpportunityTypes: settingsEnabledOpportunityTypes,
      preflightMarketRefs: settingsPreflightMarketRefs,
    }),
    [
      settingsName, settingsDescription, settingsColor, settingsType, settingsProductLine, settingsRetoolUserId,
      settingsIndustryIndices, logoUploadId, logoRemoved, settingsDefaultIndustry, settingsDefaultBrand,
      settingsIndustryDefaultBrands, settingsCustomerPartition, settingsMarketplaceInboxRows, settingsHandoffDefaults,
      settingsConversionsMonthly, settingsConversionsWeekly, settingsCurrency,
      settingsBqSyncFailureSlackAlertEnabled, settingsEnabledOpportunityTypes, settingsPreflightMarketRefs,
    ],
  );

  // ── Save (diff-based) ─────────────────────────────────────────────
  /** Resolves `true` once every pending change is saved; `false` when blocked or a save failed. */
  const handleSave = async (): Promise<boolean> => {
    setSettingsRetoolTouched(true);
    setSettingsMarketplaceInboxTouched(true);
    if (!canSave) return false;

    const { tenantChanges, settingsChanges, marketChanges } = orgSettingsChanges(
      orgSettingsFormSnapshot,
      org,
      savedSettingsForDiff,
    );
    const hasTenantChanges = Object.keys(tenantChanges).length > 0;
    const hasSettingsChanges = Object.keys(settingsChanges).length > 0;
    const nextMarketRefs = marketChanges.preflightMarketRefs;

    if (!hasTenantChanges && !hasSettingsChanges && !nextMarketRefs) {
      setSettingsRetoolTouched(false);
      return true;
    }

    const saveTenant = () =>
      updateTenantMut.mutateAsync(
        { tenantId: org.id, changes: tenantChanges },
        {
          onSuccess: () => {
            setSettingsName(settingsName.trim());
            setSettingsDescription(settingsDescription.trim());
            setSettingsRetoolTouched(false);
          },
        },
      );

    const saveSettings = () =>
      adminUpdateSettingsMut.mutateAsync(
        { tenantId: org.id, data: settingsChanges, showToast: !hasTenantChanges },
        {
          onSuccess: () => {
            if ("defaultIndustry" in settingsChanges) {
              setSettingsDefaultIndustry(settingsChanges.defaultIndustry ?? null);
            }
            if ("defaultBrand" in settingsChanges) {
              setSettingsDefaultBrand(settingsDefaultBrand.trim());
            }
            if ("customerPartition" in settingsChanges) {
              setSettingsCustomerPartition(settingsCustomerPartition.trim());
            }
            if ("marketplaceOrdersInboxEmails" in settingsChanges) {
              setSettingsMarketplaceInboxRows(
                rowsFromSaved(settingsChanges.marketplaceOrdersInboxEmails, null),
              );
              setSettingsMarketplaceInboxTouched(false);
            }
            if ("marketplaceOrderHandoffDefaults" in settingsChanges) {
              setSettingsHandoffDefaultsTouched(false);
            }
            if ("currency" in settingsChanges && settingsChanges.currency) {
              setSettingsCurrency(settingsChanges.currency);
            }
            if ("conversionsMonthly" in settingsChanges || "conversionsWeekly" in settingsChanges) {
              setConversionsSaved(true);
              setTimeout(() => setConversionsSaved(false), 2000);
            }
          },
        },
      );

    const saveMarkets = (refs: AddPreflightMarketInput[]) =>
      replaceMarketsMut.mutateAsync(
        { tenantId: org.id, refs, showToast: !hasTenantChanges && !hasSettingsChanges },
        { onSuccess: () => setSettingsPreflightMarketsTouched(false) },
      );

    // Each mutation toasts its own failure; the rejection only stops the chain.
    return saveOrgChanges({
      saveTenant: hasTenantChanges ? saveTenant : null,
      saveSettings: hasSettingsChanges ? saveSettings : null,
      saveMarkets: nextMarketRefs ? () => saveMarkets(nextMarketRefs) : null,
    }).then(
      () => true,
      () => false,
    );
  };

  const handleArchive = async () => {
    await deactivateTenantMut.mutateAsync({ tenantId: org.id });
    requestClose();
  };

  const handleRestore = async () => {
    await reactivateTenantMut.mutateAsync({ tenantId: org.id });
    // Stay open — user usually wants to keep editing the just-restored org.
  };

  const isSaving = updateTenantMut.isPending || adminUpdateSettingsMut.isPending || replaceMarketsMut.isPending;
  const isEmbedded = variant === "embedded";
  const activeTab = isEmbedded ? (tabProp ?? "general") : tab;

  const changedFields = useMemo(
    () => orgSettingsChangedFields(orgSettingsFormSnapshot, org, savedSettingsForDiff),
    [orgSettingsFormSnapshot, org, savedSettingsForDiff],
  );
  const isDirty = changedFields.length > 0;
  const isInboxChanged = changedFields.includes("marketplaceOrdersInboxEmails");
  const lastBqSyncedAt = latestSuccessfulRun(syncRunsData?.runs ?? [])?.finishedAt ?? null;

  const saveHint =
    settingsName.trim().length === 0
      ? t("iam.orgSettings.saveHint.name", "Organization name is required")
      : descOver
        ? t("iam.orgSettings.saveHint.desc", "Description is too long")
        : retoolError
          ? t("iam.orgSettings.saveHint.retool", "Fix the Retool user ID on the Organization tab")
          : marketplaceInboxError
            ? t("iam.orgSettings.saveHint.inbox", "Fix the order email CC on the Orders tab")
            : "";

  const onFormApiChangeRef = useRef(onFormApiChange);
  onFormApiChangeRef.current = onFormApiChange;
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const handleTriggerSyncRef = useRef(handleTriggerSync);
  handleTriggerSyncRef.current = handleTriggerSync;
  const canSyncWithBq =
    !isSuperTenant && !isSyncing && !isSavingPartition && !partitionIsEmpty;
  useEffect(() => {
    onFormApiChangeRef.current?.({
      isDirty,
      changedFields,
      lastBqSyncedAt,
      canSave,
      isSaving,
      handleSave: () => handleSaveRef.current(),
      saveHint,
      canSyncWithBq,
      isSyncingBq: isSyncing,
      isSavingPartitionForSync: isSavingPartition,
      syncPartitionEmpty: partitionIsEmpty,
      handleSyncWithBq: () => handleTriggerSyncRef.current(),
    });
  }, [
    isDirty,
    changedFields,
    lastBqSyncedAt,
    canSave,
    isSaving,
    saveHint,
    canSyncWithBq,
    isSyncing,
    isSavingPartition,
    partitionIsEmpty,
  ]);

  const logoFileInput = (
      <input
        ref={logoInputRef}
        type="file"
        accept={TENANT_LOGO_ACCEPT}
        className="sr-only"
        disabled={isLogoUploading || isSuperTenant}
        onChange={(e) => void handleLogoFileChange(e.target.files?.[0])}
      />
  );

  const tabList = (
          <TabsList
            variant="line"
            className={cn(
              "w-full justify-start px-5",
              // Recolor the shared active-underline indicator red while the Danger tab is active.
              tab === "danger" && "[&>[aria-hidden]]:bg-destructive",
            )}
          >
            <TabsTrigger
              value="general"
              data-testid="org-settings-tab-general"
              badge={descOver ? <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden /> : undefined}
            >
              {t("iam.orgSettings.tabs.general", "General")}
            </TabsTrigger>
            {canViewOrganizationTab && (
            <TabsTrigger
              value="organization"
              data-testid="org-settings-tab-organization"
              badge={
                settingsRetoolTouched && retoolError
                  ? <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                  : undefined
              }
            >
              {t("iam.orgSettings.tabs.organization", "Organization")}
            </TabsTrigger>
            )}
            <TabsTrigger
              value="marketplace"
              data-testid="org-settings-tab-marketplace"
              badge={
                settingsMarketplaceInboxTouched && marketplaceInboxError
                  ? <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                  : undefined
              }
            >
              {t("iam.orgSettings.tabs.marketplace", "Orders")}
            </TabsTrigger>
            <TabsTrigger value="kpis" data-testid="org-settings-tab-kpis">
              {t("iam.orgSettings.tabs.orgKpis", "Org KPIs")}
            </TabsTrigger>
            {!isSuperTenant && (
              <TabsTrigger
                value="danger"
                data-testid="org-settings-tab-danger"
                className="text-destructive/80 hover:text-destructive data-[state=active]:text-destructive"
              >
                {t("iam.orgSettings.tabs.danger", "Danger zone")}
              </TabsTrigger>
            )}
          </TabsList>
  );

  const tabPanels = (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* ── General ─────────────────────────────────────── */}
            <TabsContent value="general" className="mt-0">
              <SettingsRow
                data-testid="org-settings-row-identity"
                bordered={false}
                className="pb-4"
                title={t("iam.orgDrawer.settings.settingsRowIdentityTitle", "Identity")}
                hint={t(
                  "iam.orgDrawer.settings.settingsRowIdentityHint",
                  "How this organization is named and referenced across Adge.",
                )}
              >
                <SettingsField
                  label={t("iam.orgDrawer.settings.tenantId", "Tenant ID")}
                  hint={t("iam.orgDrawer.settings.tenantIdHint", "Read-only · use it to reference this org in API calls")}
                >
                  <div className="flex h-9 items-center overflow-hidden rounded-md border border-border bg-muted/40">
                    <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap px-3 font-mono text-xs text-muted-foreground">
                      {org.id}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyId}
                      aria-label={t("iam.orgDrawer.settings.copyTenantIdAria", "Copy tenant ID")}
                      className={cn(
                        "flex h-full shrink-0 items-center gap-1 border-l border-border px-2.5 text-[11px] font-medium transition-colors",
                        idCopied
                          ? "bg-success-subtle text-success-subtle-foreground"
                          : "bg-background text-foreground hover:bg-muted",
                      )}
                    >
                      {idCopied ? (
                        <><Check className="h-3 w-3" />{t("iam.orgDrawer.settings.copied", "Copied!")}</>
                      ) : (
                        <><Copy className="h-3 w-3" />{t("iam.orgDrawer.settings.copy", "Copy")}</>
                      )}
                    </button>
                  </div>
                </SettingsField>
              </SettingsRow>

              <LockableSection locked={isSuperTenant}>
                <SettingsRow hideHeading bordered={false} className="border-b border-border-subtle pt-0">
                  <SettingsField
                    label={t("iam.orgDrawer.settings.organizationNameLabel", "Organization name")}
                    required
                  >
                    <input
                      type="text"
                      data-testid="org-settings-name"
                      value={settingsName}
                      onChange={(e) => setSettingsName(e.target.value)}
                      disabled={isSuperTenant}
                      maxLength={255}
                      className={SETTINGS_CONTROL_CLASS}
                    />
                  </SettingsField>
                  <SettingsField label={t("iam.orgDrawer.settings.typeLabel", "Type")} required>
                    <div
                      role="radiogroup"
                      aria-label={t("iam.orgDrawer.settings.typeLabel", "Type")}
                      className="grid grid-cols-2 gap-2"
                    >
                      {TENANT_TYPES.map((tt) => {
                        const isSelected = settingsType === tt.value;
                        return (
                          <button
                            key={tt.value}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            disabled={isSuperTenant}
                            onClick={() => setSettingsType(tt.value)}
                            className={cn(
                              "flex h-10 items-center gap-2.5 rounded-md px-3 text-xs font-medium text-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                              isSelected
                                ? "border-[1.5px] border-primary bg-primary-subtle"
                                : "border border-border bg-background hover:bg-muted",
                            )}
                          >
                            <span
                              aria-hidden
                              className={cn(
                                "size-3.5 shrink-0 rounded-full bg-background",
                                isSelected ? "border-4 border-primary" : "border border-input-border",
                              )}
                            />
                            <tt.Icon
                              className={cn("size-3.5 shrink-0", isSelected ? "text-primary" : "text-muted-foreground")}
                            />
                            {t(tt.labelKey, tt.labelDefault)}
                          </button>
                        );
                      })}
                    </div>
                  </SettingsField>
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-branding"
                  title={t("iam.orgDrawer.settings.settingsRowBrandingTitle", "Branding")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowBrandingHint",
                    "Logo and accent tint the header above, org cards and avatars.",
                  )}
                >
                  <div className="flex items-center gap-3 rounded-lg border border-dashed border-input-border p-3">
                      <div className="relative shrink-0">
                        <OrgLogoMark
                          name={org.name}
                          logoUrl={displayLogoUrl}
                          accent={settingsColor ?? orgColor(org.name)}
                          size={48}
                        />
                        {isLogoUploading && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/80">
                            <span className="text-[10px] font-semibold tabular-nums text-foreground">
                              {logoUploadProgress ?? 0}%
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        {!displayLogoUrl && (
                          <p className="text-xs font-medium text-foreground">
                            {t("iam.orgDrawer.settings.logoEmptyTitle", "No logo yet — showing initials")}
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground">
                          {t(
                            "iam.orgDrawer.settings.logoDropzoneHint",
                            "Drop a PNG, JPEG, WebP or SVG here · max 2 MB",
                          )}
                        </p>
                        {isLogoUploading && (
                          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
                            <div
                              className="h-full rounded-full bg-primary transition-all duration-200"
                              style={{ width: `${logoUploadProgress ?? 0}%` }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {(displayLogoUrl ?? logoUploadId) && !isLogoUploading && (
                          <button
                            type="button"
                            disabled={isSuperTenant}
                            onClick={handleLogoRemove}
                            className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <X className="h-3 w-3" />
                            {t("iam.orgDrawer.settings.logoRemove", "Remove")}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={isLogoUploading || isSuperTenant}
                          onClick={() => logoInputRef.current?.click()}
                          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Upload className="h-3 w-3" />
                          {isLogoUploading
                            ? t("iam.orgDrawer.settings.logoUploading", "Uploading…")
                            : t("iam.orgDrawer.settings.logoUpload", "Upload")}
                        </button>
                      </div>
                  </div>
                  <AccentColorControl
                    orgName={settingsName.trim() || org.name}
                    brandColor={settingsColor}
                    onBrandColorChange={setSettingsColor}
                    initialBrandColor={org.brandColor?.trim() ?? null}
                    logoUrl={displayLogoUrl}
                    disabled={isSuperTenant}
                    layout="compact"
                  />
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-internal-note"
                  bordered={false}
                  title={t("iam.orgDrawer.settings.settingsRowInternalNoteTitle", "Internal note")}
                  hint={t("iam.orgDrawer.settings.settingsRowInternalNoteHint", "Shown to admins under the name.")}
                >
                  <div className="flex flex-col gap-1.5">
                    <textarea
                      value={settingsDescription}
                      onChange={(e) => setSettingsDescription(e.target.value)}
                      disabled={isSuperTenant}
                      maxLength={DESC_MAX}
                      rows={3}
                      aria-label={t("iam.orgDrawer.settings.descriptionLabel", "Description")}
                      placeholder={t("iam.orgDrawer.settings.descriptionPlaceholder", "Short note about this tenant…")}
                      className={cn(
                        "max-h-40 min-h-20 resize-none overflow-y-auto rounded-md border bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
                        descOver
                          ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                          : "border-input-border focus:border-ring focus:ring-ring/40",
                      )}
                    />
                    {descOver ? (
                      <p className="text-[11px] text-destructive" role="alert">
                        {t("iam.validation.descTooLong")}
                      </p>
                    ) : (
                      <p className="text-[11px] tabular-nums text-muted-foreground">
                        {t("iam.orgDrawer.settings.descriptionCount", "{{length}} / {{max}}", {
                          length: settingsDescription.length,
                          max: DESC_MAX,
                        })}
                      </p>
                    )}
                  </div>
                </SettingsRow>
              </LockableSection>
            </TabsContent>

            {/* ── Organization ────────────────────────────────── */}
            <TabsContent value="organization" className="mt-0">
              <LockableSection locked={isSuperTenant}>
                <SettingsRow
                  data-testid="org-settings-row-product"
                  title={t("iam.orgDrawer.settings.settingsRowProductTitle", "Product line")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowProductHint",
                    "Controls which KPIs and menus the tenant sees.",
                  )}
                >
                  <ToggleGroup
                    type="single"
                    variant="segmented"
                    data-testid="org-settings-product-line"
                    aria-label={t("iam.orgDrawer.settings.settingsRowProductTitle", "Product line")}
                    value={settingsProductLine}
                    onValueChange={(v) => { if (v) setSettingsProductLine(v as TenantProductLine); }}
                    disabled={isSuperTenant}
                    className={cn(SEGMENT_SLIDING_TRACK_CLASS, "grid-cols-3 p-1")}
                    indicatorClassName={SEGMENT_SLIDING_INDICATOR_CLASS}
                  >
                    {PRODUCT_LINE_OPTIONS.map((option) => (
                      <ToggleGroupItem
                        key={option.value}
                        value={option.value}
                        variant="default"
                        className={cn(SEGMENT_SLIDING_ITEM_CLASS, "h-auto flex-col gap-0.5 py-2")}
                      >
                        <span className="text-xs font-medium">{t(option.labelKey, option.labelDefault)}</span>
                        <span className="text-[11px] font-normal text-muted-foreground">
                          {t(option.hintKey, option.hintDefault)}
                        </span>
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-opportunity-types"
                  title={t("iam.orgDrawer.settings.settingsRowOpportunityTypesTitle", "Opportunity types")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowOpportunityTypesHint",
                    "Which types this organization can create. Existing sets stay available.",
                  )}
                >
                  <div className="grid gap-3 sm:grid-cols-2">
                    {(
                      [
                        ["PERSONA", "iam.orgDrawer.settings.opportunityTypePersona", "Persona"],
                        ["COMPETITOR", "iam.orgDrawer.settings.opportunityTypeCompetitor", "Competitor"],
                        ["USER_GENERATED", "iam.orgDrawer.settings.opportunityTypeCustom", "Custom"],
                        ["POST_FLIGHT", "iam.orgDrawer.settings.opportunityTypePostFlight", "Post-Flight"],
                      ] as const
                    ).map(([type, labelKey, labelDefault]) => {
                      const inputId = `org-settings-opportunity-type-${type}`;
                      return (
                        <div key={type} className="flex items-center gap-2">
                          <Switch
                            id={inputId}
                            data-testid={inputId}
                            checked={settingsEnabledOpportunityTypes[type]}
                            disabled={isSuperTenant || isLoadingAdminSettings}
                            onCheckedChange={(checked) =>
                              setSettingsEnabledOpportunityTypes((current) => ({ ...current, [type]: checked }))
                            }
                          />
                          <label htmlFor={inputId} className="text-xs font-medium text-foreground">
                            {t(labelKey, labelDefault)}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-industries"
                  title={t("iam.orgDrawer.settings.settingsRowIndustriesTitle", "Industries")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowIndustriesStarredHint",
                    "The starred one opens by default in Create Opportunity Set. Each can pre-select a My Brand on Dashboard, Overview and Drilldown.",
                  )}
                  headingExtra={
                    <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
                      <Database className="mt-0.5 size-3 shrink-0" aria-hidden />
                      {t(
                        "iam.orgDrawer.settings.industriesCatalogNote",
                        "Catalog comes from BigQuery. Missing one? Run Sync with BQ below.",
                      )}
                    </p>
                  }
                >
                  <OrgIndustriesSettingsTable
                    selected={settingsIndustryIndices}
                    defaultIndustry={settingsDefaultIndustry}
                    industryDefaultBrands={settingsIndustryDefaultBrands}
                    industries={industries}
                    disabled={isSuperTenant}
                    brandSelectEnabled={canViewOrganizationTab}
                    onSelectedChange={(next) => {
                      setSettingsIndustryIndices(next);
                      if (settingsDefaultIndustry && !next.includes(settingsDefaultIndustry)) {
                        setSettingsDefaultIndustry(next.length === 1 ? next[0]! : null);
                      }
                    }}
                    onDefaultIndustryChange={setSettingsDefaultIndustry}
                    onIndustryDefaultBrandChange={(industry, brand) =>
                      setSettingsIndustryDefaultBrands((prev) => ({ ...prev, [industry]: brand }))
                    }
                  />
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-brand"
                  title={t("iam.orgDrawer.settings.settingsRowBrandTitle", "Brand")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowBrandHint",
                    "This organization's own brand. Pre Flight brands are managed on the Pre Flight page.",
                  )}
                >
                  <SettingsField
                    label={t("iam.orgDrawer.settings.defaultBrandLabel", "Default brand")}
                    hint={t(
                      "iam.orgDrawer.settings.defaultBrandHint",
                      "My Brand in Explore and Opportunities when an industry has none of its own",
                    )}
                  >
                    {isLoadingAdminSettings ? (
                      <div className="h-9 max-w-[320px] rounded-md border border-border bg-muted/40 animate-pulse" />
                    ) : (
                      <input
                        type="text"
                        data-testid="org-settings-default-brand"
                        placeholder={t("iam.orgDrawer.settings.defaultBrandPlaceholder", "e.g. Vodafone")}
                        value={settingsDefaultBrand}
                        onChange={(e) => setSettingsDefaultBrand(e.target.value)}
                        disabled={isSuperTenant}
                        className={cn(SETTINGS_CONTROL_CLASS, "max-w-[320px] border-input-border font-mono text-xs")}
                      />
                    )}
                  </SettingsField>
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-preflight"
                  title={t("iam.orgDrawer.settings.settingsRowPreFlightTitle", "Pre Flight")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowPreFlightHint",
                    "The markets a report can be filed under.",
                  )}
                >
                  <SettingsField label={t("iam.orgDrawer.settings.marketsLabel", "Markets")}>
                    {isLoadingMarketRefs ? (
                      <div className="h-11 rounded-md border border-border bg-muted/40 animate-pulse" />
                    ) : (
                      <MarketMultiSelect
                        layout="chips"
                        selected={settingsPreflightMarketRefs}
                        onChange={(next) => {
                          setSettingsPreflightMarketsTouched(true);
                          setSettingsPreflightMarketRefs(next);
                        }}
                        disabled={isSuperTenant}
                        ariaLabel={t("iam.orgDrawer.settings.marketsAddAria", "Add markets")}
                      />
                    )}
                  </SettingsField>
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-display-defaults"
                  title={t("iam.orgDrawer.settings.settingsRowDisplayDefaultsTitle", "Display defaults")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowDisplayDefaultsHint",
                    "Starting values for new users on this tenant.",
                  )}
                >
                  <SettingsField
                    className="sm:max-w-[calc(50%-0.5rem)]"
                    label={t("iam.orgDrawer.settings.displayCurrencyLabel", "Display currency")}
                    hint={t(
                      "iam.orgDrawer.settings.displayCurrencyHint",
                      "Symbol only — values aren't converted",
                    )}
                  >
                    <select
                      data-testid="org-settings-currency"
                      value={settingsCurrency}
                      onChange={(e) => setSettingsCurrency(e.target.value as CurrencyCode)}
                      disabled={isSuperTenant}
                      className={cn(SETTINGS_CONTROL_CLASS, "border-input-border text-[13px]")}
                    >
                      {CURRENCY_DROPDOWN_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>{option.label}</option>
                      ))}
                    </select>
                  </SettingsField>
                </SettingsRow>

                <OrgBigQuerySyncSection
                  isSuperTenant={isSuperTenant}
                  isLoadingAdminSettings={isLoadingAdminSettings}
                  customerPartition={settingsCustomerPartition}
                  onCustomerPartitionChange={setSettingsCustomerPartition}
                  conversionsWeekly={settingsConversionsWeekly}
                  conversionsMonthly={settingsConversionsMonthly}
                  onConversionsWeeklyChange={setSettingsConversionsWeekly}
                  onConversionsMonthlyChange={setSettingsConversionsMonthly}
                  conversionsSaved={conversionsSaved}
                  bqCatalogAsOfAt={adminSettings?.bqCatalogAsOfAt ?? null}
                  isFailureSlackAlertEnabled={settingsBqSyncFailureSlackAlertEnabled}
                  onFailureSlackAlertEnabledChange={setSettingsBqSyncFailureSlackAlertEnabled}
                  members={members}
                  productTypes={productTypes}
                  skuProducts={skuProducts}
                  syncRuns={syncRunsData?.runs ?? []}
                  syncRunsTotalCount={syncRunsData?.totalCount ?? 0}
                  isLoadingSyncRuns={isLoadingSyncRuns}
                  latestRun={syncRunsData?.runs[0] ?? null}
                  isSyncing={isSyncing}
                  isSavingPartition={isSavingPartition}
                  partitionIsEmpty={partitionIsEmpty}
                  onTriggerSync={handleTriggerSync}
                />

                <LegacyRetoolSettingsRow
                  value={settingsRetoolUserId}
                  onChange={setSettingsRetoolUserId}
                  onBlur={() => setSettingsRetoolTouched(true)}
                  error={retoolError}
                  touched={settingsRetoolTouched}
                  disabled={isSuperTenant}
                />
              </LockableSection>
            </TabsContent>

            {/* ── Marketplace ─────────────────────────────────── */}
            <TabsContent value="marketplace" className="mt-0">
              <LockableSection locked={isSuperTenant}>
                <SettingsRow
                  data-testid="org-settings-row-order-emails"
                  title={t("iam.orgDrawer.settings.settingsRowOrderEmailsTitle", "Order emails")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowOrderEmailsHint",
                    "Who is copied on every Marketplace order email.",
                  )}
                >
                  <SettingsField
                    label={t("iam.orgDrawer.settings.marketplaceOrdersInboxLabel", "CC on order emails")}
                    htmlFor="org-settings-marketplace-orders-inbox"
                    extra={
                      isInboxChanged ? (
                        <span className="text-[11px] leading-snug text-warning-subtle-foreground">
                          {t("iam.orgDrawer.settings.fieldChanged", "Changed")}
                        </span>
                      ) : null
                    }
                    error={settingsMarketplaceInboxTouched && marketplaceInboxError ? marketplaceInboxError : undefined}
                    hint={
                      settingsMarketplaceInboxTouched && marketplaceInboxError
                        ? undefined
                        : t(
                            "iam.orgDrawer.settings.marketplaceOrdersInboxHint",
                            "Press Enter to add. Leave empty to copy only the person placing the order.",
                          )
                    }
                  >
                    {isLoadingAdminSettings ? (
                      <div className="h-9 rounded-md border border-border bg-muted/40 animate-pulse" />
                    ) : (
                      <MultiTagInput
                        id="org-settings-marketplace-orders-inbox"
                        data-testid="org-settings-marketplace-orders-inbox"
                        disableDropdown
                        disabled={isSuperTenant}
                        className={
                          settingsMarketplaceInboxTouched && marketplaceInboxError
                            ? "border-error"
                            : undefined
                        }
                        value={emailsFromRows(settingsMarketplaceInboxRows)}
                        onChange={(emails) => {
                          setSettingsMarketplaceInboxTouched(true);
                          const next = emails.slice(0, MARKETPLACE_ORDERS_INBOX_EMAIL_COUNT_MAX);
                          setSettingsMarketplaceInboxRows(rowsFromSaved(next, null));
                        }}
                        placeholder={t(
                          "iam.orgDrawer.settings.marketplaceOrdersInboxPlaceholder",
                          "orders@company.com",
                        )}
                        addFreeTextLabel={(email) =>
                          t("iam.orgDrawer.settings.marketplaceOrdersInboxAddNamed", {
                            email,
                            defaultValue: "Add {{email}}",
                          })
                        }
                      />
                    )}
                  </SettingsField>
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-brand-rules"
                  title={t("iam.orgDrawer.settings.settingsRowBrandRulesTitle", "Brand rules")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowBrandRulesHint",
                    "Default guidelines sent to production partners. Each order can adjust them.",
                  )}
                  headingExtra={<HandoffSectionProgress section="rules" value={settingsHandoffDefaults} />}
                >
                  <MarketplaceOrderHandoffDefaultsForm
                    section="rules"
                    layout="plain"
                    value={settingsHandoffDefaults}
                    onChange={(next) => {
                      setSettingsHandoffDefaultsTouched(true);
                      setSettingsHandoffDefaults(next);
                    }}
                    disabled={isSuperTenant}
                  />
                </SettingsRow>

                <SettingsRow
                  data-testid="org-settings-row-brand-kit"
                  bordered={false}
                  title={t("iam.orgDrawer.settings.settingsRowBrandKitTitle", "Brand kit")}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowBrandKitHint",
                    "Folders or links to brand assets the partner uses during production.",
                  )}
                  headingExtra={<HandoffSectionProgress section="brandKit" value={settingsHandoffDefaults} />}
                >
                  <MarketplaceOrderHandoffDefaultsForm
                    section="brandKit"
                    layout="plain"
                    value={settingsHandoffDefaults}
                    onChange={(next) => {
                      setSettingsHandoffDefaultsTouched(true);
                      setSettingsHandoffDefaults(next);
                    }}
                    disabled={isSuperTenant}
                  />
                </SettingsRow>
              </LockableSection>
            </TabsContent>

            <TabsContent value="kpis" className="mt-0">
              <LockableSection locked={isSuperTenant}>
                <div data-testid="org-settings-row-kpi-catalog" className="px-8 py-6">
                  <OrgKpiSettingsTab tenantId={org.id} locked={isSuperTenant || isArchived} />
                </div>
              </LockableSection>
            </TabsContent>

            {/* ── Danger zone ─────────────────────────────────── */}
            {!isSuperTenant && (
              <TabsContent value="danger" className="mt-0">
                <SettingsRow
                  data-testid="org-settings-row-danger"
                  bordered={false}
                  title={t("iam.orgDrawer.settings.settingsRowArchiveTitle", {
                    orgName: org.name,
                    defaultValue: "Archive {{orgName}}",
                  })}
                  hint={t(
                    "iam.orgDrawer.settings.settingsRowArchiveHint",
                    "Archiving removes active access. Settings, roles, KPIs, and orders are kept and return on restore.",
                  )}
                >
                  {isArchived ? (
                    <div className="rounded-lg border border-border bg-muted/30 p-4">
                      <p className="mb-1 text-xs font-semibold">{t("iam.orgDrawer.restoreZone.title", "Archived organization")}</p>
                      <p className="mb-3 text-[11px] text-muted-foreground">
                        {t(
                          "iam.orgDrawer.restoreZone.body",
                          "This organization is archived and hidden from the active list. Restore it to make it visible and editable again.",
                        )}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        disabled={reactivateTenantMut.isPending}
                        onClick={() => void handleRestore()}
                      >
                        <ArchiveRestore className="h-3.5 w-3.5" />
                        {reactivateTenantMut.isPending
                          ? t("iam.orgDrawer.restoreZone.pending", "Restoring…")
                          : t("iam.orgDrawer.restoreZone.confirm", "Restore organization")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      <div className="rounded-lg border border-border bg-muted/20 p-4">
                        <p className="mb-2 text-xs font-semibold text-foreground">
                          {t("iam.orgDrawer.settings.archiveWhatHappensTitle", "What happens")}
                        </p>
                        <ul className="space-y-2 text-[11px] leading-relaxed text-muted-foreground">
                          <li>{t("iam.orgDrawer.settings.archiveEffectMembers", "Members lose access immediately.")}</li>
                          <li>
                            {t(
                              "iam.orgDrawer.settings.archiveEffectData",
                              "Settings, roles, KPIs, and orders are kept — restore brings them back.",
                            )}
                          </li>
                          <li>{t("iam.orgDrawer.settings.archiveEffectRestore", "Restore anytime from Organizations → Archived.")}</li>
                        </ul>
                      </div>
                      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                        {confirmArchive ? (
                          <div className="flex flex-col gap-2.5">
                            <p className="text-[11px] text-muted-foreground">
                              {t("iam.orgDrawer.settings.archiveWarning", "This will archive the organization and remove all active access. You can restore it later from the archived list.")}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button
                                disabled={deactivateTenantMut.isPending}
                                onClick={() => void handleArchive()}
                                variant="destructive"
                                size="sm"
                                className="h-8 px-3 text-xs"
                              >
                                {deactivateTenantMut.isPending ? t("iam.orgDrawer.settings.archiving", "Archiving…") : t("iam.orgDrawer.settings.confirmArchive", "Confirm archive")}
                              </Button>
                              <button
                                type="button"
                                onClick={() => setConfirmArchive(false)}
                                className="text-[11px] text-muted-foreground hover:text-foreground"
                              >
                                {t("common.cancel", "Cancel")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => setConfirmArchive(true)}
                          >
                            <Archive className="h-3.5 w-3.5" />
                            {t("iam.orgDrawer.settings.archiveOrganization", "Archive organization")}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </SettingsRow>
              </TabsContent>
            )}
          </div>
  );

  const tabs = (
    <Tabs
      value={activeTab}
      onValueChange={(v) => { if (!isEmbedded) setTab(v as SettingsTab); }}
      className="min-h-0 flex-1 gap-0 overflow-hidden"
    >
      {!isEmbedded && tabList}
      {tabPanels}
    </Tabs>
  );

  if (isEmbedded) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {logoFileInput}
        {tabs}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
      {logoFileInput}
      <DialogContent variant="form" className="flex h-[70vh] flex-col gap-0 overflow-hidden p-0" {...PREVENT_DISMISS_PROPS}>
        <DialogHeader className="flex-row items-center gap-3 space-y-0 p-5 pb-4">
          <OrgLogoMark
            name={org.name}
            logoUrl={displayLogoUrl}
            accent={accentColor}
            isSuperTenant={isSuperTenant}
            size={36}
          />
          <div className="min-w-0">
            <DialogTitle className="truncate text-[15px] font-semibold">{org.name}</DialogTitle>
            <DialogDescription>{industryLabel(org.type)} · {t("iam.orgSettings.subtitle", "Organization settings")}</DialogDescription>
          </div>
        </DialogHeader>
        {tabs}
        <DialogFooter className="!mx-0 !mb-0 items-center">
          {!isSuperTenant && isDirty && !canSave && !isSaving && (
            <span className="mr-auto text-[11px] text-muted-foreground">
              {saveHint}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={requestClose}>
            {t("common.close", "Close")}
          </Button>
          <Button
            size="sm"
            className="gap-1.5"
            data-testid="org-settings-save"
            disabled={isSuperTenant || isArchived || !isDirty || !canSave}
            onClick={() => void handleSave()}
          >
            {isSaving ? t("iam.orgDrawer.settings.saving", "Saving…") : t("iam.orgDrawer.settings.saveChanges", "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
